import { offlineStore } from './OfflineStore';

export interface DownloadStatus {
    bookId: string;
    state: 'idle' | 'downloading' | 'paused' | 'completed' | 'error';
    progress: number; // 0-100
    totalChunks: number;
    downloadedChunks: number;
    error?: string;
}

export type DownloadListener = (status: DownloadStatus) => void;

class DownloadManagerService {
    private queue: Map<string, DownloadStatus> = new Map();
    private controllers: Map<string, AbortController> = new Map();
    private listeners: Set<DownloadListener> = new Set();

    addListener(l: DownloadListener) { this.listeners.add(l); }
    removeListener(l: DownloadListener) { this.listeners.delete(l); }
    
    private notify(s: DownloadStatus) {
        this.queue.set(s.bookId, s);
        this.listeners.forEach(l => l(s));
    }

    async getStatus(bookId: string): Promise<DownloadStatus> {
        if (this.queue.has(bookId)) return this.queue.get(bookId)!;

        const hasAudio = await offlineStore.hasBookOffline(bookId);
        if (hasAudio) {
            const progress = await offlineStore.getBookOfflineProgress(bookId);
            return {
                bookId,
                state: progress.downloaded === progress.total && progress.total > 0 ? 'completed' : 'idle',
                progress: progress.total > 0 ? Math.round((progress.downloaded / progress.total) * 100) : 0,
                totalChunks: progress.total,
                downloadedChunks: progress.downloaded
            };
        }

        return {
            bookId, state: 'idle', progress: 0, totalChunks: 0, downloadedChunks: 0
        };
    }

    async startDownload(bookId: string, playlistUrl: string) {
        if (this.queue.get(bookId)?.state === 'downloading') return;

        const controller = new AbortController();
        this.controllers.set(bookId, controller);

        const status: DownloadStatus = {
            bookId, state: 'downloading', progress: 0, totalChunks: 0, downloadedChunks: 0
        };
        this.notify(status);

        try {
            const res = await fetch(playlistUrl);
            if (!res.ok) throw new Error('Failed to fetch playlist');
            const data = await res.json();
            
            // Check tracks
            let rawTracks: any[] = [];
            if (Array.isArray(data)) rawTracks = data;
            else if (data && Array.isArray(data.tracks)) rawTracks = data.tracks;
            else if (data && Array.isArray(data.items)) rawTracks = data.items;
            else if (data && Array.isArray(data.playlist)) rawTracks = data.playlist;

            const tracks = rawTracks.filter((t: any) => {
                const url = typeof t === 'string' ? t : (t.url || t.src || t.uri);
                return url && url.match(/\.(mp3|m4a|wav|ogg)/i);
            });

            if (tracks.length === 0) {
                throw new Error("Playlist has no valid audio tracks to download.");
            }

            try {
                // Save playlist
                const playlistBlob = new Blob([JSON.stringify({ ok: true, tracks })], { type: 'application/json' });
                await offlineStore.saveChunk(bookId, 'playlist.json', playlistBlob, 'pinned');
            } catch (e) {
                console.error("Failed to save playlist.json offline", e);
            }

            status.totalChunks = tracks.length;
            this.notify({ ...status });

            const concurrency = 3;
            const chunks = [...tracks];
            let completed = 0;

            const processChunk = async (track: any) => {
                if (controller.signal.aborted) return;

                const trackUrl = typeof track === 'string' ? track : (track.url || track.src || track.uri);
                const absoluteTrackUrl = new URL(trackUrl, playlistUrl).href;

                let filename = '';
                if (track.key && /\.(mp3|m4a|wav|ogg)$/i.test(track.key)) {
                    filename = track.key.split('/').pop() || 'audio.mp3';
                } else {
                    const urlPath = absoluteTrackUrl.split('?')[0];
                    filename = urlPath.split('/').pop() || 'audio.mp3';
                }

                // Check if already downloaded
                const existing = await offlineStore.getChunk(bookId, filename);
                if (existing) {
                    completed++;
                    status.downloadedChunks = completed;
                    status.progress = Math.round((completed / status.totalChunks) * 100);
                    this.notify({ ...status });
                    return;
                }

                // Download
                const chunkRes = await fetch(absoluteTrackUrl, { signal: controller.signal });
                if (!chunkRes.ok) throw new Error(`Failed to download chunk ${filename}`);
                
                const blob = await chunkRes.blob();
                await offlineStore.saveChunk(bookId, filename, blob, 'pinned');

                completed++;
                status.downloadedChunks = completed;
                status.progress = Math.round((completed / status.totalChunks) * 100);
                this.notify({ ...status });
            };

            // Process chunks concurrently
            const workers: Promise<void>[] = [];
            for (let i = 0; i < concurrency; i++) {
                workers.push((async () => {
                    while (chunks.length > 0 && !controller.signal.aborted) {
                        const chunk = chunks.shift();
                        if (chunk) {
                            await processChunk(chunk);
                        }
                    }
                })());
            }

            await Promise.all(workers);

            if (controller.signal.aborted) {
                status.state = 'paused';
                this.notify({ ...status });
            } else {
                status.state = 'completed';
                status.progress = 100;
                this.notify({ ...status });
            }
        } catch (e: any) {
            console.error("Download failed:", e);
            status.state = 'error';
            status.error = e.message || 'Download failed';
            this.notify({ ...status });
        } finally {
            this.controllers.delete(bookId);
        }
    }

    cancelDownload(bookId: string) {
        const controller = this.controllers.get(bookId);
        if (controller) {
            controller.abort();
            this.controllers.delete(bookId);
        }
        const status = this.queue.get(bookId);
        if (status) {
            status.state = 'paused';
            this.notify({ ...status });
        }
    }

    async removeDownload(bookId: string) {
        this.cancelDownload(bookId);
        await offlineStore.deleteBookOffline(bookId);
        const status: DownloadStatus = {
            bookId, state: 'idle', progress: 0, totalChunks: 0, downloadedChunks: 0
        };
        this.notify(status);
    }
}

export const downloadManager = new DownloadManagerService();
